import {
  AlertTriangle,
  CalendarClock,
  Check,
  Clock,
  DatabaseZap,
  Inbox,
  Loader2,
  MailX,
  Sparkles,
  TrendingUp,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { openRenewDialog } from '@/components/access/useAccess';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { OPEN_REPORTS_EVENT, openTopicGuide } from '@/components/welcome/api';

import { Notice, NoticeAction, dismiss, markRead, whenLabel } from './api';

/**
 * One card, shared by the bell's popover and the /notifications page.
 *
 * The icon and its colour say what kind of news it is before a word is read:
 * red for something of ours that is broken, amber for something that wants
 * the customer, green for "fixed", neutral for plain information.
 */
function Icon({ n }: { n: Notice }) {
  let tone = 'bg-primary/10 text-primary';
  let Glyph = Sparkles;
  if (n.state === 'ended') {
    tone = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400';
    Glyph = Check;
    if (n.kind === 'anomalies_week') {
      tone = 'bg-muted text-muted-foreground';
      Glyph = TrendingUp;
    }
  } else if (n.level === 'attention') {
    tone = 'bg-destructive/10 text-destructive';
    Glyph =
      n.kind === 'report_late'
        ? Clock
        : n.kind === 'data_stale'
          ? DatabaseZap
          : MailX;
  } else if (n.level === 'action') {
    tone = 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
    Glyph =
      n.kind === 'anomalies_week'
        ? TrendingUp
        : n.kind === 'access_ending'
          ? CalendarClock
          : AlertTriangle;
  } else if (n.kind === 'report_empty') {
    tone = 'bg-muted text-muted-foreground';
    Glyph = Inbox;
  }
  return (
    <span
      className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg ${tone}`}
      aria-hidden
    >
      <Glyph className="size-4" />
    </span>
  );
}

/** Runs a card's button. Returns the email dialog to render beside the list. */
export function useNoticeAction(onDone?: () => void) {
  const navigate = useNavigate();
  const [email, setEmail] = useState<{ id: number; title?: string } | null>(
    null
  );

  const run = (a: NoticeAction) => {
    onDone?.();
    switch (a.type) {
      case 'reports':
        window.dispatchEvent(new Event(OPEN_REPORTS_EVENT));
        break;
      case 'email':
        setEmail({ id: a.report_id, title: a.title });
        break;
      case 'route':
        navigate(a.to);
        break;
      case 'renew':
        openRenewDialog();
        break;
      case 'topics':
        openTopicGuide(a.topic);
        break;
    }
  };

  const dialog = (
    <EmailDialog
      report={email}
      onOpenChange={(open) => (!open ? setEmail(null) : undefined)}
    />
  );
  return { run, dialog };
}

/** The email exactly as it was sent, in a frame that runs no scripts. */
function EmailDialog({
  report,
  onOpenChange
}: {
  report: { id: number; title?: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const id = report?.id ?? null;

  useEffect(() => {
    if (id == null) return;
    let cancelled = false;
    setHtml(null);
    setFailed(false);
    fetch(`/api/reports/${id}/last-email`, { credentials: 'include' })
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((t) => !cancelled && setHtml(t))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <Dialog open={!!report} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b">
          <DialogTitle className="text-base">Email as it was sent</DialogTitle>
          {report?.title ? (
            <p dir="auto" className="truncate text-sm text-muted-foreground">
              {report.title}
            </p>
          ) : null}
        </DialogHeader>
        <div className="h-[70vh] bg-white">
          {html ? (
            <iframe
              title="Email as it was sent"
              sandbox=""
              srcDoc={html}
              className="w-full h-full border-0"
            />
          ) : failed ? (
            <p className="p-6 text-sm text-muted-foreground">
              This email is no longer available.
            </p>
          ) : (
            <div className="grid h-full place-items-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NoticeCard({
  n,
  onAction,
  compact = false
}: {
  n: Notice;
  onAction: (a: NoticeAction) => void;
  compact?: boolean;
}) {
  const open = () => markRead([n.id]);
  return (
    <div
      className={`flex gap-3 px-4 py-3 ${
        n.unread ? 'bg-primary/[0.05]' : ''
      }`}
      onClick={open}
    >
      <Icon n={n} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p
            className={`flex-1 text-sm leading-snug ${
              n.unread ? 'font-semibold' : 'font-medium'
            }`}
          >
            {n.title}
            {n.state === 'ended' && n.kind !== 'anomalies_week' ? (
              <span className="ml-2 rounded px-1.5 py-px align-[1px] text-[10px] font-bold uppercase tracking-wide bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                Fixed
              </span>
            ) : null}
            {n.state === 'open' && n.level === 'action' && n.kind !== 'anomalies_week' ? (
              <span className="ml-2 rounded px-1.5 py-px align-[1px] text-[10px] font-bold uppercase tracking-wide bg-amber-500/10 text-amber-700 dark:text-amber-400">
                Action
              </span>
            ) : null}
          </p>
          {n.unread ? (
            <span
              className="mt-1.5 size-2 shrink-0 rounded-full bg-primary"
              aria-label="Unread"
            />
          ) : null}
        </div>
        {n.subject ? (
          // dir="auto": a name written in Arabic reads right to left on its
          // own line instead of scrambling the English around it.
          <p
            dir="auto"
            className="mt-0.5 truncate text-left text-[13px] font-medium text-foreground/80"
          >
            {n.subject}
          </p>
        ) : null}
        <p
          className={`mt-0.5 text-[13px] leading-snug text-muted-foreground ${
            compact ? 'line-clamp-3' : ''
          }`}
        >
          {n.body}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{whenLabel(n.at)}</span>
          {n.cta ? (
            <button
              type="button"
              className="rounded-md border px-2 py-0.5 font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={(e) => {
                e.stopPropagation();
                open();
                onAction(n.cta!.action);
              }}
            >
              {n.cta.label}
            </button>
          ) : null}
          {n.dismissible && !compact ? (
            <button
              type="button"
              className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(n.id);
              }}
            >
              <X className="size-3" />
              Dismiss
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
